/**
 * GridCrash — Application Entry & Orchestrator
 *
 * Loop 11 additions:
 * - Mascot click opens emoji picker tab inside Scores modal
 * - Red/white/blue canvas fireworks on WIN
 * - Duplicate code removed, single clean init
 */

import { GridCrashEngine, DIFFICULTIES, type DifficultyConfig, type GenerationMode } from "./game/engine.js";
import { BoardUI, detectInputType } from "./ui/board.js";
import { ActionBar } from "./ui/action_bar.js";
import { hapticLight, hapticHeavy, hapticSuccess } from "./game/haptics.js";
import { HUD } from "./ui/hud.js";
import { ModalManager } from "./ui/modal.js";
import { recordGameResult, type Achievement } from "./game/achievements.js";
import {
  addToLifetimeScore,
  getActiveEmoji,
  getLifetimeScore,
  setActiveEmoji,
  getUnlockedEmojis,
  getLifetimeScore as getLT,
  getNextUnlockThreshold,
  getTierProgress,
  setExtraEmojiProvider,
} from "./game/unlocks.js";
import {
  initSound,
  playExplosion,
  playFireworks,
  playCascade,
  playHint,
  playTremor,
} from "./game/sound.js";
import { getEffectiveCoins } from "./game/catalog.js";
import { creditBonus } from "./game/wallet.js";
import {
  getTodaySeed,
  startDailyAttempt,
  completeDailyAttempt,
  hasDailyAttempt,
  type DailyTier,
} from "./game/daily.js";
import {
  VersusMatch,
  type CpuSkill,
  type MatchDetail,
} from "./game/versus.js";
import { playWinAnimation } from "./ui/win_fx.js";
import { getThemeMascot } from "./game/board_themes.js";
import {
  replayRecorder,
  submitReplayIfTop100,
  fetchDailyGhost,
  fetchReplayStream,
  type ReplayMeta,
  type ReplayEvent,
} from "./game/replay.js";
import { GhostRace, type GhostSnapshot } from "./game/ghost_race.js";
import { showRaceBar, updateRaceBar, showRaceResult, hideRaceBar } from "./ui/race_bar.js";
import { getLastUsedName } from "./game/global_leaderboard.js";
import { rewardTierFor, spendCoins } from "./game/profile.js";
import { getOwnedPackEmojis } from "./game/catalog.js";
import { recordGameForQuests } from "./game/quests.js";
import { Sidebar } from "./ui/sidebar.js";
import {
  CAMPAIGN_SIZE,
  getCampaignLevel,
  getLevelStars,
  isLevelUnlocked,
  recordLevelResult,
  toDifficultyConfig,
  type LevelOutcome,
} from "./game/campaign.js";
import {
  RELICS,
  getRun,
  startRun,
  abandonRun,
  hasRelic,
  buyRelic,
  canSeismographSave,
  markSeismographUsed,
  dowsingAvailable,
  markDowsingUsed,
  floorConfig,
  addFloorLoot,
  descend,
  bankRun,
  bustRun,
  getStats as getGauntletStats,
} from "./game/gauntlet.js";

// ─── DOM Setup ────────────────────────────────────────────────────────────────

function getEl<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Required element #${id} missing from DOM`);
  return el as T;
}

// ─── Application State ────────────────────────────────────────────────────────

let engine: GridCrashEngine;
let generationMode: GenerationMode = "random";
let currentDifficulty: DifficultyConfig = DIFFICULTIES["easy"];
let endShown = false;
let stopFireworksSound: (() => void) | null = null;
let activeMatch: VersusMatch | null = null;

// ── Ghost racing (Phase 7) ──
let activeGhost: GhostRace | null = null;
let ghostDismissed = false;
/** Refreshes the player's race-bar lane on live-engine state changes. */
let raceBarTick: (() => void) | null = null;

// Share-grid tracking: opening reveal and (on a loss) the fatal mine.
// Reset whenever a new game of any mode starts.
let shareStartCell: [number, number] | null = null;
let shareFatalCell: [number, number] | null = null;

function resetShareCells(): void {
  shareStartCell = null;
  shareFatalCell = null;
}

// ── Hint lifeline (6A) ──
// Escalating per-game cost keeps hints a spice, not a crutch. Disabled in
// competitive modes (daily, versus) for leaderboard integrity.
const HINT_COSTS = [25, 50, 100];
let hintsUsedThisGame = 0;
let hintsAllowed = false;

// True while the current engine is a Gauntlet floor (6D).
let gauntletGame = false;

function currentHintCost(): number {
  return HINT_COSTS[Math.min(hintsUsedThisGame, HINT_COSTS.length - 1)];
}

/** Dowsing Rod relic makes the first hint of a Gauntlet floor free. */
function effectiveHintCost(): number {
  if (gauntletGame && dowsingAvailable()) return 0;
  return currentHintCost();
}

/**
 * Per-game trackers: share cells + replay recorder. Call on every game
 * start. Starting anything that isn't the next Gauntlet floor forfeits
 * an active run — one life is the contract.
 */
function resetGameTracking(keepGauntletRun = false): void {
  resetShareCells();
  hintsUsedThisGame = 0;
  if (!keepGauntletRun) {
    abandonRun();
    gauntletGame = false;
  }
  activeGhost?.destroy();
  activeGhost = null;
  ghostDismissed = false;
  raceBarTick = null;
  hideRaceBar();
  replayRecorder.reset(currentDifficulty.rows, currentDifficulty.cols);
}

/**
 * Ghost-win bonus farm guard: +5 coins only the first time a given
 * ghost is beaten per UTC day — "Race again" keeps the bragging
 * banner but not the payout.
 */
function grantGhostWinBonus(replayId: string): boolean {
  const KEY = "gridcrash.ghostwins.v1";
  const today = new Date().toISOString().slice(0, 10);
  let state: { date: string; ids: string[] } = { date: today, ids: [] };
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { date?: string; ids?: string[] };
      if (parsed.date === today && Array.isArray(parsed.ids)) {
        state = { date: today, ids: parsed.ids.filter((i): i is string => typeof i === "string") };
      }
    }
  } catch { /* corrupt — start fresh */ }
  if (state.ids.includes(replayId)) return false;
  state.ids.push(replayId);
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* memory-only session */ }
  return true;
}

/**
 * Every game gets a fresh seed so its replay can rebuild the exact
 * board (mine placement depends only on seed + first click).
 */
function newGameSeed(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] || 1;
}

/**
 * Submits a winning run's input stream if it ranks top-100 for its
 * difficulty. Fire-and-forget — the game never waits on it.
 */
function submitWinReplay(
  score: number,
  timeElapsed: number,
  difficultyId: string,
  isDaily: boolean,
  seed: number,
  mode: "random" | "deterministic"
): void {
  void submitReplayIfTop100({
    difficulty: rewardTierFor(difficultyId, isDaily),
    finalScore: score,
    durationMs: timeElapsed * 1000,
    seed: String(seed),
    events: replayRecorder.finish(),
    mode,
    engineDifficulty: difficultyId,
    playerName: getLastUsedName() || null,
  }).catch(() => {});
}

/**
 * Ascending musical run for cascade reveals — more cells opened means a
 * longer, faster climb up the pentatonic ladder (see playCascade).
 */
function playCascadeRun(cellsOpened: number): void {
  const noteCount = Math.min(8, 2 + Math.floor(cellsOpened / 5));
  const gapMs = cellsOpened >= 30 ? 45 : 70;
  for (let i = 0; i < noteCount; i++) {
    setTimeout(() => playCascade(i), i * gapMs);
  }
}

// ─── Cosmetics helpers ───────────────────────────────────────────────────────

/** Equipped theme mascot wins over the unlock-picker mascot. */
function activeMascotEmoji(): string {
  return getThemeMascot() ?? getActiveEmoji();
}

/**
 * Credits Clash Coins for a finished game. Server-computed and
 * rate-limited when connected; offline the local wallet earns the same
 * formula and reconciles at next sync.
 */
function awardCoins(score: number, difficultyId: string, isDaily: boolean): void {
  void import("./game/profile.js")
    .then(async (m) => {
      const result = await m.awardRewards(score, m.rewardTierFor(difficultyId, isDaily));
      if (result && result.coinsEarned > 0) {
        showToast(`🪙 +${result.coinsEarned} Clash Coin${result.coinsEarned === 1 ? "" : "s"}`);
      } else if (!result) {
        const { creditLocal } = await import("./game/wallet.js");
        const coins = creditLocal(score);
        showToast(`🪙 +${coins} Clash Coin${coins === 1 ? "" : "s"}`);
      }
    })
    .catch(() => {});
}

// ─── Toast Notifications ──────────────────────────────────────────────────────

function showToast(message: string): void {
  document.querySelector(".unlock-toast")?.remove();

  const toast = document.createElement("div");
  toast.className = "unlock-toast";
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.textContent = message;
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add("unlock-toast--visible"));
  });

  setTimeout(() => {
    toast.classList.remove("unlock-toast--visible");
    toast.addEventListener("transitionend", () => toast.remove(), { once: true });
  }, 3000);
}

// ─── Achievement Toast ────────────────────────────────────────────────────────

function showAchievementToast(achievement: Achievement): void {
  document.querySelector(".achievement-toast")?.remove();

  const toast = document.createElement("div");
  toast.className = "achievement-toast";
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.textContent = `🏅 Achievement: ${achievement.emoji} ${achievement.name}`;
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => toast.classList.add("achievement-toast--visible"));
  });

  setTimeout(() => {
    toast.classList.remove("achievement-toast--visible");
    toast.addEventListener("transitionend", () => toast.remove(), { once: true });
  }, 3500);
}

// ─── Emoji Picker Panel ───────────────────────────────────────────────────────

/**
 * Builds the emoji picker HTML injected into the Scores modal.
 * Called by ModalManager.showMascotPicker().
 */
export function buildEmojiPickerHTML(onSelect: (emoji: string) => void): HTMLElement {
  const unlocked  = getUnlockedEmojis();
  const active    = getActiveEmoji();
  const lifetime  = getLT();
  const nextThresh = getNextUnlockThreshold();
  const progress   = getTierProgress();

  const allEmojis = [
    "😐","🙂","😎","🤔","😤","🧠","🔥","⚡","💀","🎯",
    "🏆","👑","🌟","🚀","💎","🌈","🎰","🧬","🌌","👾",
  ];

  // Store-bought mascot packs render after the score unlocks.
  for (const packEmoji of getOwnedPackEmojis()) {
    if (!allEmojis.includes(packEmoji)) allEmojis.push(packEmoji);
  }

  const wrap = document.createElement("div");
  wrap.className = "emoji-picker";

  // ── Progress bar ──
  const progressPct = Math.round(progress * 100);
  const nextLabel   = nextThresh > 0
    ? `${lifetime} / ${nextThresh} pts to next unlock`
    : "All mascots unlocked! 👾";

  wrap.innerHTML = `
    <div class="emoji-picker__progress-wrap">
      <div class="emoji-picker__progress-label">
        <span class="emoji-picker__lifetime">⚡ ${lifetime} lifetime pts</span>
        <span class="emoji-picker__next">${nextLabel}</span>
      </div>
      <div class="emoji-picker__bar-track">
        <div class="emoji-picker__bar-fill" style="width:${progressPct}%"></div>
      </div>
    </div>
    <p class="emoji-picker__hint">Tap an unlocked mascot to equip it</p>
    <div class="emoji-picker__grid" id="emoji-picker-grid"></div>
  `;

  // ── Emoji grid ──
  const grid = wrap.querySelector<HTMLElement>("#emoji-picker-grid")!;

  allEmojis.forEach((emoji) => {
    const isUnlocked = unlocked.includes(emoji);
    const isActive   = emoji === active;

    const btn = document.createElement("button");
    btn.className = "emoji-picker__cell" +
      (isUnlocked  ? " emoji-picker__cell--unlocked"  : " emoji-picker__cell--locked") +
      (isActive    ? " emoji-picker__cell--active"    : "");
    btn.setAttribute("aria-label", isUnlocked ? `Select ${emoji}` : "Locked");
    btn.disabled = !isUnlocked;

    btn.innerHTML = isUnlocked
      ? emoji
      : `<span class="emoji-picker__lock">🔒</span>`;

    if (isUnlocked) {
      btn.addEventListener("click", () => {
        // Update active state visually
        grid.querySelectorAll(".emoji-picker__cell--active")
            .forEach(el => el.classList.remove("emoji-picker__cell--active"));
        btn.classList.add("emoji-picker__cell--active");
        onSelect(emoji);
      });
    }

    grid.appendChild(btn);
  });

  return wrap;
}

// ─── Mascot Header Element ────────────────────────────────────────────────────

function initMascot(header: HTMLElement): HTMLElement {
  const mascot = document.createElement("button");
  mascot.className = "mascot-btn";
  mascot.id = "mascot-btn";
  mascot.setAttribute("aria-label", "Your mascot — tap to change");
  mascot.title = `Lifetime score: ${getLifetimeScore()} pts`;
  mascot.textContent = activeMascotEmoji();

  const actions = header.querySelector(".page-header__actions");
  if (actions) {
    header.insertBefore(mascot, actions);
  } else {
    header.appendChild(mascot);
  }

  return mascot;
}

function updateMascot(mascot: HTMLElement, phase: string, justUnlocked: string[]): void {
  mascot.textContent = activeMascotEmoji();
  mascot.title = `Lifetime score: ${getLifetimeScore()} pts`;

  mascot.classList.remove("mascot-btn--win", "mascot-btn--lose", "mascot-btn--unlock");

  if (justUnlocked.length > 0) {
    mascot.classList.add("mascot-btn--unlock");
    mascot.textContent = justUnlocked[justUnlocked.length - 1];
  } else if (phase === "WIN") {
    mascot.classList.add("mascot-btn--win");
  } else if (phase === "LOSE") {
    mascot.classList.add("mascot-btn--lose");
  }
}

// ─── Hints Footer ─────────────────────────────────────────────────────────────

function renderHints(container: HTMLElement, type: "touch" | "mouse"): void {
  if (type === "touch") {
    container.innerHTML = `
      <span class="keyboard-hint"><span class="hint-icon">⛏️</span> Dig mode: tap to reveal</span>
      <span class="keyboard-hint"><span class="hint-icon">🚩</span> Flag mode: tap to flag</span>
      <span class="keyboard-hint"><span class="hint-icon">👇</span> Switch modes in the bar below</span>
    `;
    container.setAttribute("aria-label", "Touch controls");
  } else {
    container.innerHTML = `
      <span class="keyboard-hint"><kbd>← ↑ → ↓</kbd> Navigate</span>
      <span class="keyboard-hint"><kbd>Enter</kbd> Reveal</span>
      <span class="keyboard-hint"><kbd>Right-click</kbd> Flag</span>
      <span class="keyboard-hint"><kbd>Space</kbd> Flag</span>
      <span class="keyboard-hint"><kbd>N</kbd> New game</span>
      <span class="keyboard-hint"><kbd>R</kbd> Restart</span>
      <span class="keyboard-hint"><kbd>?</kbd> Help</span>
    `;
    container.setAttribute("aria-label", "Keyboard shortcuts");
  }
}

// ─── Initialisation ───────────────────────────────────────────────────────────

function init(): void {
  // Anonymous identity + profile sync — fire-and-forget. The game never
  // waits on this and plays fully offline if it fails.
  void import("./game/profile.js")
    .then((m) => m.initProfile())
    .catch(() => {});

  // Purchased mascot packs extend the emoji picker (provider injection
  // avoids a profile ↔ unlocks import cycle).
  setExtraEmojiProvider(getOwnedPackEmojis);

  const appRoot  = getEl<HTMLElement>("app");
  const headerEl = getEl<HTMLElement>("page-header");
  const hudEl    = getEl<HTMLElement>("hud");
  const boardEl  = getEl<HTMLElement>("board");
  const helpBtn  = getEl<HTMLButtonElement>("help-btn");
  const lbBtn    = getEl<HTMLButtonElement>("leaderboard-btn");
  const hintsEl  = getEl<HTMLElement>("hints-footer");

  // ── Wide-screen sidebar (renders only at ≥1100px) ──
  const sidebar = new Sidebar(getEl<HTMLElement>("sidebar"));

  // ── Mascot ──
  const mascot = initMascot(headerEl);

  // ── Mobile Dig/Flag action bar (visible on touch devices only) ──
  const actionBar = new ActionBar((mode) => {
    board.setFlagMode(mode === "flag");
  });

  // ── Hints ──
  let detectedType = detectInputType();
  renderHints(hintsEl, detectedType);
  actionBar.setVisible(detectedType === "touch");

  const upgradeHints = (e: PointerEvent): void => {
    const newType = e.pointerType === "mouse" ? "mouse" : "touch";
    if (newType !== detectedType) {
      detectedType = newType;
      renderHints(hintsEl, detectedType);
      actionBar.setVisible(detectedType === "touch");
    }
    // Unlock AudioContext on first user gesture (browser requirement)
    initSound();
    window.removeEventListener("pointerdown", upgradeHints);
  };
  window.addEventListener("pointerdown", upgradeHints, { once: false });

  // ── Modal ──
  const modal = new ModalManager(appRoot, {
    onNewGame: () => startGame(),
    onChangeDifficulty: (d) => startGame(d),
    onStartDailyTier: (tier) => startDailyGame(tier),
    getShareCells: () => ({ start: shareStartCell, fatal: shareFatalCell }),
    onCosmeticsChanged: () => {
      board.update(engine.getState());
      mascot.textContent = activeMascotEmoji();
    },
    onQuestsChanged: () => {
      hud.refreshQuestChip();
      sidebar.renderQuests();
    },
    onStartCampaignLevel: (levelNum) => startCampaignLevel(levelNum),
    onStartGhostRace: (meta, events) => startGhostRaceGame(meta, events),
  });

  // ── Mascot click → emoji picker tab in Scores modal ──
  mascot.addEventListener("click", () => {
    modal.showMascotPicker(
      currentDifficulty.leaderboardKey,
      currentDifficulty.label,
      (selectedEmoji) => {
        setActiveEmoji(selectedEmoji);
        mascot.textContent = activeMascotEmoji();
        mascot.title = `Lifetime score: ${getLifetimeScore()} pts`;
      }
    );
  });

  // ── HUD ──
  const hud = new HUD(hudEl, {
    onNewGame: (d) => startGame(d),
    onModeChange: (m) => {
      generationMode = m;
      startGame(currentDifficulty);
    },
    onDailyChallenge: () => modal.showDailyChallenge(),
    onVersus: () => showVersusSetup(),
    onQuests: () => modal.showQuests(currentDifficulty.leaderboardKey, currentDifficulty.label),
    onHint: () => void useHint(),
    onCampaign: () => void modal.showCampaign(),
    onAftershock: () => showAftershockSetup(),
    onGauntlet: () => showGauntletIntro(),
  });

  // ── Per-mode HUD chrome: hint button + tremor countdown + gauntlet pot ──
  function refreshModeChrome(): void {
    const state = engine.getState();
    const cost = effectiveHintCost();
    hud.updateHintButton(
      hintsAllowed && (state.phase === "IDLE" || state.phase === "PLAY"),
      cost,
      state.phase === "PLAY" && (cost === 0 || getEffectiveCoins() >= cost)
    );
    hud.updateTremor(engine.tremorCountdown);
    const run = getRun();
    hud.updateGauntlet(gauntletGame && run ? run.floor : null, run?.pot);
  }

  let hintInFlight = false;

  async function useHint(): Promise<void> {
    if (hintInFlight) return;
    if (!hintsAllowed || engine.getState().phase !== "PLAY") return;

    const cost = effectiveHintCost();
    if (cost > 0 && getEffectiveCoins() < cost) {
      showToast("❌ Not enough Clash Coins");
      return;
    }

    if (cost === 0) {
      // Dowsing Rod's free floor hint — doesn't escalate paid costs.
      markDowsingUsed();
    } else {
      // Server-side spend when online (coins live in clash_coins after
      // the wallet fold); local wallet covers offline play.
      hintInFlight = true;
      const paid = await spendCoins(cost);
      hintInFlight = false;
      if (!paid) {
        showToast("❌ Not enough Clash Coins");
        return;
      }
      hintsUsedThisGame++;
    }

    // Re-derive the target after the async spend — a timed mode could
    // have ended the game mid-flight.
    const target = engine.getState().phase === "PLAY" ? engine.findHintCell() : null;
    if (!target) {
      if (cost > 0) creditBonus(cost); // refund the orphaned spend
      return;
    }

    // A hint is just a reveal the player paid for — recording it as a
    // normal "r" event keeps ghost replays byte-compatible.
    replayRecorder.record("r", target.row, target.col);
    engine.revealCell(target.row, target.col);
    board.pulseHint(target.row, target.col);
    playHint();
    showToast(cost === 0
      ? "🪄 Dowsing Rod found a safe cell — free!"
      : `💡 Safe cell revealed — 🪙 −${cost}`);
    refreshModeChrome();
  }

  // ── Quests ──
  // One feed point for all three game-end paths (solo, daily, versus).
  function updateQuestsAfterGame(
    won: boolean,
    difficultyId: string,
    timeSecs: number,
    score: number,
    correctFlags: number,
    isDaily: boolean
  ): void {
    const update = recordGameForQuests({
      won, difficultyId, timeSecs, correctFlags, score, isDaily,
    });
    update.justCompleted.forEach((q, i) => {
      setTimeout(() => {
        showToast(`🎯 Quest complete: ${q.label} — claim 🪙${q.reward} in Quests!`);
      }, 400 + i * 1400);
    });
    if (update.streakBonus > 0) {
      setTimeout(() => {
        showToast(`🔥 ${update.streakCount}-day streak! +${update.streakBonus} Clash Coins`);
      }, 3200);
    }
    if (update.saverConsumed) {
      setTimeout(() => {
        showToast(`🛟 Streak saver used — your ${update.streakCount}-day streak survives!`);
      }, 4600);
    }
    if (update.saverEarned) {
      setTimeout(() => showToast("🛟 Streak saver earned!"), 6000);
    }
    hud.refreshQuestChip();
    sidebar.renderQuests();
  }

  // ── Board ──
  const board = new BoardUI(boardEl, {
    onReveal: (row, col) => {
      const pre = engine.getState();
      const wasIdle = pre.phase === "IDLE";
      const safeBefore = pre.safeCellsRemaining;

      // Gauntlet Seismograph (6D): absorb one fatal tap per run.
      if (gauntletGame && pre.phase === "PLAY") {
        const cell = pre.grid[row][col];
        if (cell.isMine && !cell.isRevealed && !cell.isFlagged && canSeismographSave()) {
          markSeismographUsed();
          engine.disarmMine(row, col);
          engine.revealCell(row, col);
          showToast("🧿 Seismograph absorbed a mine!");
          return;
        }
      }

      if (wasIdle || pre.phase === "PLAY") replayRecorder.record("r", row, col);

      const result = engine.revealCell(row, col);

      if (wasIdle && result !== "NO_CHANGE") shareStartCell = [row, col];
      if (result === "LOST") shareFatalCell = [row, col];

      if (result === "REVEALED_SAFE") {
        hapticLight();
        const opened = safeBefore - engine.getState().safeCellsRemaining;
        if (opened >= 4) playCascadeRun(opened);
      }
    },
    onFlag:   (row, col) => {
      if (engine.getState().phase === "PLAY") replayRecorder.record("f", row, col);
      engine.toggleFlag(row, col);
    },
    onSound:  (sound) => {
      if (sound === "flag") {
        // Import lazily so sound module is only loaded after first gesture
        import("./game/sound.js").then(s => s.playFlag()).catch(() => {});
      }
    },
  });

  // ── Header buttons ──
  helpBtn.addEventListener("click", () => modal.showHelp());
  lbBtn.addEventListener("click", () =>
    modal.showLeaderboard(currentDifficulty.leaderboardKey, currentDifficulty.label)
  );

  // ── Keyboard shortcuts ──
  document.addEventListener("keydown", (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLButtonElement) return;
    if (e.key === "?")              modal.showHelp();
    else if (e.key === "n" || e.key === "N") startGame();
    else if (e.key === "r" || e.key === "R") startGame(currentDifficulty);
  });

  // ── Game starter ──
  function startGame(difficulty?: DifficultyConfig): void {
    if (difficulty) currentDifficulty = difficulty;
    endShown = false;
    resetGameTracking();
    hintsAllowed = true;
    modal.close();

    // Tear down any active versus match — solo play is fully independent.
    activeMatch?.abort();
    activeMatch = null;
    hud.hideOpponentPanel();

    // Clean up any leftover fireworks
    document.getElementById("fireworks-canvas")?.remove();
    stopFireworksSound?.();
    stopFireworksSound = null;

    mascot.textContent = activeMascotEmoji();
    mascot.classList.remove("mascot-btn--win", "mascot-btn--lose", "mascot-btn--unlock");

    // Sever old engine callbacks BEFORE replacing the reference.
    // Without this the old engine's 500ms timer can fire one last time after
    // the new engine is assigned, pushing stale grid state into the fresh board
    // and causing the flicker/spaz seen on the 3rd game onwards.
    if (engine) {
      engine.onStateChange = null;
      engine.onGameEnded   = null;
    }

    const gameSeed = newGameSeed();
    const gameMode = generationMode;
    engine = new GridCrashEngine(currentDifficulty, gameMode, gameSeed);

    engine.onStateChange = (state) => {
      board.update(state);
      hud.update(state, engine.minesRemaining);
      sidebar.update(state, engine.minesRemaining);
      refreshModeChrome();
      activeGhost?.onPlayerState(state);
      raceBarTick?.();

      if ((state.phase === "WIN" || state.phase === "LOSE") && !endShown) {
        endShown = true;

        const unlockResult = addToLifetimeScore(state.score);
        awardCoins(state.score, state.difficulty.id, false);
        if (state.phase === "WIN") {
          submitWinReplay(state.score, state.timeElapsed, state.difficulty.id, false, gameSeed, gameMode);
        }
        updateMascot(mascot, state.phase, unlockResult.justUnlocked);

        if (unlockResult.justUnlocked.length > 0) {
          unlockResult.justUnlocked.forEach((emoji) => {
            showToast(`🎉 New mascot unlocked: ${emoji}  Tap the mascot to equip it!`);
          });
        }

        // ── Achievements ──
        let correctFlags = 0;
        for (const row of state.grid) {
          for (const cell of row) {
            if (cell.isFlagged && cell.isMine) correctFlags++;
          }
        }
        const achieveResult = recordGameResult({
          won: state.phase === "WIN",
          difficultyId: state.difficulty.id,
          correctFlags,
          lifetimeScore: unlockResult.newTotal,
        });
        if (achieveResult.justEarned.length > 0) {
          achieveResult.justEarned.forEach((a, i) => {
            setTimeout(() => showAchievementToast(a), i * 1200);
          });
        }

        updateQuestsAfterGame(
          state.phase === "WIN", state.difficulty.id, state.timeElapsed,
          state.score, correctFlags, false
        );

        // Fireworks on WIN 🎆
        if (state.phase === "WIN") {
          playWinAnimation(10000);
          stopFireworksSound = playFireworks(10000);
          hapticSuccess();
        }

        // Explosion sound on LOSE 💥
        if (state.phase === "LOSE") {
          playExplosion();
          hapticHeavy();
        }

        setTimeout(() => modal.showGameEnd(state, generationMode), 600);
      }
    };

    engine.onGameEnded = (_score, _key) => { /* handled in modal */ };

    const initialState = engine.getState();
    board.initialize(initialState);
    hud.update(initialState, engine.minesRemaining);
    sidebar.update(initialState, engine.minesRemaining);
    refreshModeChrome();
  }

  // ── Ghost racing ──

  /**
   * Attaches a validated ghost to the current game: race bar, live
   * progress wiring, and the settle-up on game end. resetGameTracking
   * (called by every game starter) is the teardown.
   */
  function attachGhost(meta: ReplayMeta, events: ReplayEvent[], retryable: boolean, msLate?: number): void {
    const ghost = new GhostRace(meta, events);
    activeGhost = ghost;

    let lastSnap: GhostSnapshot = { progress: 0, liveScore: 0, finished: false };
    const playerProgress = (): number => {
      const s = engine.getState();
      const d = s.difficulty;
      return Math.min(1, Math.max(0, 1 - s.safeCellsRemaining / (d.rows * d.cols - d.mineCount)));
    };

    ghost.onProgress = (snap) => {
      lastSnap = snap;
      updateRaceBar(playerProgress(), snap);
    };
    raceBarTick = () => updateRaceBar(playerProgress(), lastSnap);

    ghost.onRaceEnd = (outcome, playerScore, ghostScore) => {
      showRaceResult(outcome, playerScore, ghostScore);
      if (outcome === "player" && grantGhostWinBonus(ghost.replayId)) {
        creditBonus(5);
        showToast(`🏁 Beat ${ghost.ghostName}'s ghost! 🪙 +5`);
      }
    };

    showRaceBar({
      ghostName: ghost.ghostName,
      ghostFinalScore: ghost.ghostFinalScore,
      retryable,
      onDismiss: () => {
        activeGhost?.destroy();
        activeGhost = null;
        raceBarTick = null;
        ghostDismissed = true;
      },
      onRaceAgain: retryable ? () => startGhostRaceGame(meta, events) : undefined,
    });

    if (msLate !== undefined && msLate > 0) ghost.attachLate(msLate);
  }

  /** Solo race against a Top Runs replay — fresh board, retryable. */
  function startGhostRaceGame(meta: ReplayMeta, events: ReplayEvent[]): void {
    if (!GhostRace.validate(meta, events)) {
      showToast("❌ Could not load that replay");
      return;
    }
    startGame(DIFFICULTIES[meta.engine_difficulty!]);
    attachGhost(meta, events, true);
  }

  // Dev-only race hook: fabricate a ghost from a raw stream so racing
  // can be exercised with zero network (verify-skill entry point).
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>)["__gridclashGhostRace"] = (
      events: ReplayEvent[],
      difficultyId: string,
      seed: number,
      finalScore: number,
      name?: string
    ): void => {
      startGhostRaceGame(
        {
          id: `dev-${Date.now()}`,
          player_name: name ?? "DEVGHOST",
          final_score: finalScore,
          duration_ms: events.length > 0 ? events[events.length - 1].t : 0,
          seed: String(seed),
          mode: "random",
          engine_difficulty: difficultyId,
          created_at: new Date().toISOString(),
        },
        events
      );
    };
  }

  // ── Daily game starter ──
  // Completely separate from startGame — uses a seeded board, tracks attempt,
  // never interferes with normal New Game / difficulty selection flow.
  function startDailyGame(tier: DailyTier): void {
    if (hasDailyAttempt(tier)) return;

    const tierMap: Record<DailyTier, string> = {
      easy: "easy", medium: "medium", hard: "hard",
    };
    currentDifficulty = DIFFICULTIES[tierMap[tier]];
    endShown    = false;
    resetGameTracking();
    hintsAllowed = false; // competitive mode — no paid reveals

    document.getElementById("fireworks-canvas")?.remove();
    stopFireworksSound?.();
    stopFireworksSound = null;

    mascot.textContent = activeMascotEmoji();
    mascot.classList.remove("mascot-btn--win", "mascot-btn--lose", "mascot-btn--unlock");

    // Sever old engine callbacks before replacing (same stale-state fix as startGame)
    if (engine) {
      engine.onStateChange = null;
      engine.onGameEnded   = null;
    }

    // Seed the engine so every player gets the same board today
    const seed = getTodaySeed(tier);
    engine = new GridCrashEngine(currentDifficulty, "random", seed);

    let attemptStarted = false;
    let firstRevealAt: number | null = null;

    // Today's best ghost races alongside (fail-soft: no ghost, no bar).
    // The engine reference is the game token — a mode switch or new game
    // before the fetch resolves silently drops the attach.
    const myEngine = engine;
    void (async () => {
      try {
        const meta = await fetchDailyGhost(String(seed));
        if (!meta || engine !== myEngine || endShown || ghostDismissed) return;
        const events = await fetchReplayStream(meta.id);
        if (!events || engine !== myEngine || endShown || ghostDismissed) return;
        if (!GhostRace.validate(meta, events)) return;
        attachGhost(
          meta, events, false,
          firstRevealAt !== null ? performance.now() - firstRevealAt : undefined
        );
      } catch { /* offline — daily plays normally */ }
    })();

    engine.onStateChange = (state) => {
      board.update(state);
      hud.update(state, engine.minesRemaining);
      sidebar.update(state, engine.minesRemaining);
      activeGhost?.onPlayerState(state);
      raceBarTick?.();

      // Record attempt start on first cell reveal
      if (state.phase === "PLAY" && !attemptStarted) {
        attemptStarted = true;
        firstRevealAt = performance.now();
        startDailyAttempt(tier);
      }

      if ((state.phase === "WIN" || state.phase === "LOSE") && !endShown) {
        endShown = true;

        const dailyProgress = completeDailyAttempt(tier, state.score, state.phase === "WIN", state.timeElapsed);

        const unlockResult = addToLifetimeScore(state.score);
        awardCoins(state.score, state.difficulty.id, true);
        if (state.phase === "WIN") {
          submitWinReplay(state.score, state.timeElapsed, state.difficulty.id, true, seed, "random");
        }
        updateMascot(mascot, state.phase, unlockResult.justUnlocked);

        if (unlockResult.justUnlocked.length > 0) {
          unlockResult.justUnlocked.forEach((emoji) => {
            showToast(`🎉 New mascot unlocked: ${emoji}  Tap the mascot to equip it!`);
          });
        }

        let correctFlags = 0;
        for (const row of state.grid)
          for (const cell of row)
            if (cell.isFlagged && cell.isMine) correctFlags++;

        const achieveResult = recordGameResult({
          won: state.phase === "WIN",
          difficultyId: state.difficulty.id,
          correctFlags,
          lifetimeScore: unlockResult.newTotal,
        });
        if (achieveResult.justEarned.length > 0) {
          achieveResult.justEarned.forEach((a, i) => {
            setTimeout(() => showAchievementToast(a), i * 1200);
          });
        }

        updateQuestsAfterGame(
          state.phase === "WIN", state.difficulty.id, state.timeElapsed,
          state.score, correctFlags, true
        );

        if (state.phase === "WIN") {
          playWinAnimation(10000);
          stopFireworksSound = playFireworks(10000);
          hapticSuccess();
        }
        if (state.phase === "LOSE") {
          playExplosion();
          hapticHeavy();
        }

        // If all 3 tiers are now complete, surface the daily-complete modal with
        // the combined score and the submit prompt right here — the player won't
        // reopen the Daily Challenge screen, so the prompt must come to them.
        // Otherwise, show the normal per-game end modal.
        if (dailyProgress.allComplete) {
          setTimeout(() => modal.showDailyComplete(), 600);
        } else {
          setTimeout(() => modal.showGameEnd(state, "random"), 600);
        }
      }
    };

    engine.onGameEnded = (_score, _key) => {};

    const initialState = engine.getState();
    board.initialize(initialState);
    hud.update(initialState, engine.minesRemaining);
    sidebar.update(initialState, engine.minesRemaining);
    refreshModeChrome();
  }

  // ── Aftershock setup: pick the board density (6C) ──
  function showAftershockSetup(): void {
    document.querySelector(".versus-overlay")?.remove();

    const overlay = document.createElement("div");
    overlay.className = "versus-overlay";
    overlay.innerHTML = `
      <div class="versus-card">
        <div class="versus-card__head">
          <h2>🌋 Aftershock</h2>
          <button class="versus-card__close" aria-label="Close">✕</button>
        </div>
        <p class="versus-card__blurb">
          The board fights back: every few reveals a <strong>tremor shifts the
          mines</strong> and the numbers recompute. Revealed and flagged cells are
          bedrock — mines can't enter them. Flag early to lock the map down.
        </p>
        <p class="versus-card__sub">Choose your fault line:</p>
        <div class="versus-card__tiers">
          <button class="versus-tier" data-diff="easy">
            <span class="versus-tier__emoji">🟢</span>
            <span class="versus-tier__name">Minor Quake</span>
            <span class="versus-tier__detail">9×9 · 10 mines · tremor every 8</span>
          </button>
          <button class="versus-tier" data-diff="medium">
            <span class="versus-tier__emoji">🟠</span>
            <span class="versus-tier__name">Major Quake</span>
            <span class="versus-tier__detail">16×16 · 40 mines · tremor every 10</span>
          </button>
          <button class="versus-tier" data-diff="hard">
            <span class="versus-tier__emoji">🔴</span>
            <span class="versus-tier__name">The Big One</span>
            <span class="versus-tier__detail">16×30 · 99 mines · tremor every 12</span>
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => overlay.remove();
    overlay.querySelector(".versus-card__close")!.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });

    overlay.querySelectorAll<HTMLButtonElement>(".versus-tier").forEach((btn) => {
      btn.addEventListener("click", () => {
        const diffId = btn.dataset["diff"]!;
        close();
        startAftershockGame(diffId);
      });
    });
  }

  // ── Aftershock game starter (6C) ──
  // Seeded so the tremor sequence is deterministic per game. No replay
  // submission — the playback engine doesn't simulate tremors.
  function startAftershockGame(diffId: string): void {
    const tremorEvery: Record<string, number> = { easy: 8, medium: 10, hard: 12 };
    currentDifficulty = DIFFICULTIES[diffId];
    endShown = false;
    resetGameTracking();
    hintsAllowed = true;
    modal.close();

    activeMatch?.abort();
    activeMatch = null;
    hud.hideOpponentPanel();

    document.getElementById("fireworks-canvas")?.remove();
    stopFireworksSound?.();
    stopFireworksSound = null;

    mascot.textContent = activeMascotEmoji();
    mascot.classList.remove("mascot-btn--win", "mascot-btn--lose", "mascot-btn--unlock");

    // Sever old engine callbacks before replacing (same stale-state fix as startGame)
    if (engine) {
      engine.onStateChange = null;
      engine.onGameEnded   = null;
    }

    engine = new GridCrashEngine(currentDifficulty, "random", newGameSeed(), {
      revealsPerTremor: tremorEvery[diffId] ?? 10,
    });

    engine.onTremor = () => {
      board.tremorShake();
      playTremor();
    };

    engine.onStateChange = (state) => {
      board.update(state);
      hud.update(state, engine.minesRemaining);
      sidebar.update(state, engine.minesRemaining);
      refreshModeChrome();

      if ((state.phase === "WIN" || state.phase === "LOSE") && !endShown) {
        endShown = true;

        const unlockResult = addToLifetimeScore(state.score);
        awardCoins(state.score, state.difficulty.id, false);
        updateMascot(mascot, state.phase, unlockResult.justUnlocked);

        if (unlockResult.justUnlocked.length > 0) {
          unlockResult.justUnlocked.forEach((emoji) => {
            showToast(`🎉 New mascot unlocked: ${emoji}  Tap the mascot to equip it!`);
          });
        }

        let correctFlags = 0;
        for (const row of state.grid)
          for (const cell of row)
            if (cell.isFlagged && cell.isMine) correctFlags++;

        const achieveResult = recordGameResult({
          won: state.phase === "WIN",
          difficultyId: state.difficulty.id,
          correctFlags,
          lifetimeScore: unlockResult.newTotal,
        });
        if (achieveResult.justEarned.length > 0) {
          achieveResult.justEarned.forEach((a, i) => {
            setTimeout(() => showAchievementToast(a), i * 1200);
          });
        }

        updateQuestsAfterGame(
          state.phase === "WIN", state.difficulty.id, state.timeElapsed,
          state.score, correctFlags, false
        );

        if (state.phase === "WIN") {
          playWinAnimation(10000);
          stopFireworksSound = playFireworks(10000);
          hapticSuccess();
        }
        if (state.phase === "LOSE") {
          playExplosion();
          hapticHeavy();
        }

        setTimeout(() => modal.showGameEnd(state, "random"), 600);
      }
    };

    engine.onGameEnded = (_score, _key) => {};

    const initialState = engine.getState();
    board.initialize(initialState);
    hud.update(initialState, engine.minesRemaining);
    sidebar.update(initialState, engine.minesRemaining);
    refreshModeChrome();
  }

  // ── Gauntlet intro: rules + lifetime stats (6D) ──
  function showGauntletIntro(): void {
    document.querySelector(".versus-overlay")?.remove();

    const stats = getGauntletStats();
    const overlay = document.createElement("div");
    overlay.className = "versus-overlay";
    overlay.innerHTML = `
      <div class="versus-card">
        <div class="versus-card__head">
          <h2>⛏️ The Gauntlet</h2>
          <button class="versus-card__close" aria-label="Close">✕</button>
        </div>
        <p class="versus-card__blurb">
          One life. Each cleared floor pays loot into your <strong>run pot</strong> —
          then you choose: <strong>bank it</strong> and walk, or <strong>descend</strong>
          for ×1.25 and a nastier board. Hit a mine and the pot is gone.
          Relics bought between floors tilt the odds. Floors 8+ are timed.
        </p>
        ${stats.runs > 0 ? `
        <div class="gauntlet-stats">
          <span>🏆 Best floor: <strong>${stats.bestFloor}</strong></span>
          <span>🏦 Biggest bank: <strong>🪙 ${stats.biggestBank}</strong></span>
          <span>🔁 Runs: <strong>${stats.runs}</strong></span>
        </div>` : ""}
        <div class="versus-card__actions">
          <button class="versus-card__btn versus-card__btn--rematch gauntlet-start">Start Run ⛏️</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => overlay.remove();
    overlay.querySelector(".versus-card__close")!.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    overlay.querySelector(".gauntlet-start")!.addEventListener("click", () => {
      close();
      startRun();
      startGauntletFloor();
    });
  }

  // ── Gauntlet floor starter (6D) ──
  // Floor loot replaces the normal coin award; no replay submission
  // (generated floor boards aren't leaderboard runs).
  function startGauntletFloor(): void {
    const run = getRun();
    if (!run) return;

    currentDifficulty = floorConfig(run.floor);
    endShown = false;
    resetGameTracking(true);
    gauntletGame = true;
    hintsAllowed = true;
    modal.close();

    activeMatch?.abort();
    activeMatch = null;
    hud.hideOpponentPanel();

    document.getElementById("fireworks-canvas")?.remove();
    stopFireworksSound?.();
    stopFireworksSound = null;

    mascot.textContent = activeMascotEmoji();
    mascot.classList.remove("mascot-btn--win", "mascot-btn--lose", "mascot-btn--unlock");

    // Sever old engine callbacks before replacing (same stale-state fix as startGame)
    if (engine) {
      engine.onStateChange = null;
      engine.onGameEnded   = null;
    }

    engine = new GridCrashEngine(currentDifficulty, "random", newGameSeed());

    engine.onStateChange = (state) => {
      board.update(state);
      hud.update(state, engine.minesRemaining);
      sidebar.update(state, engine.minesRemaining);
      refreshModeChrome();

      if ((state.phase === "WIN" || state.phase === "LOSE") && !endShown) {
        endShown = true;

        const unlockResult = addToLifetimeScore(state.score);
        updateMascot(mascot, state.phase, unlockResult.justUnlocked);

        let correctFlags = 0;
        for (const row of state.grid)
          for (const cell of row)
            if (cell.isFlagged && cell.isMine) correctFlags++;

        const achieveResult = recordGameResult({
          won: state.phase === "WIN",
          difficultyId: state.difficulty.id,
          correctFlags,
          lifetimeScore: unlockResult.newTotal,
        });
        if (achieveResult.justEarned.length > 0) {
          achieveResult.justEarned.forEach((a, i) => {
            setTimeout(() => showAchievementToast(a), i * 1200);
          });
        }

        updateQuestsAfterGame(
          state.phase === "WIN", state.difficulty.id, state.timeElapsed,
          state.score, correctFlags, false
        );

        if (state.phase === "WIN") {
          hapticSuccess();
          const loot = addFloorLoot();
          setTimeout(() => showGauntletIntermission(loot), 600);
        } else {
          playExplosion();
          hapticHeavy();
          const floorReached = run.floor;
          const potLost = run.pot;
          bustRun();
          gauntletGame = false;
          setTimeout(() => showGauntletRunOver(floorReached, potLost, state.timeElapsed), 600);
        }
      }
    };

    engine.onGameEnded = (_score, _key) => {};

    const initialState = engine.getState();
    board.initialize(initialState);
    hud.update(initialState, engine.minesRemaining);
    sidebar.update(initialState, engine.minesRemaining);
    refreshModeChrome();
  }

  // ── Gauntlet intermission: loot, relic shop, bank or descend (6D) ──
  function showGauntletIntermission(loot: number): void {
    const run = getRun();
    if (!run) return;

    document.querySelector(".versus-overlay")?.remove();

    const overlay = document.createElement("div");
    overlay.className = "versus-overlay";

    const draw = (): void => {
      const current = getRun();
      if (!current) return;

      const relicShop = RELICS
        .filter((r) => !hasRelic(r.id))
        .map((r) => `
          <div class="gauntlet-relic">
            <span class="gauntlet-relic__emoji">${r.emoji}</span>
            <span class="gauntlet-relic__body">
              <span class="gauntlet-relic__name">${r.name}</span>
              <span class="gauntlet-relic__desc">${r.description}</span>
            </span>
            <button class="modal-btn modal-btn--ghost gauntlet-relic__buy"
                    data-relic="${r.id}" ${current.pot < r.price ? "disabled" : ""}>
              🪙 ${r.price}
            </button>
          </div>`)
        .join("");

      const owned = current.relics
        .map((id) => RELICS.find((r) => r.id === id))
        .filter((r): r is NonNullable<typeof r> => !!r)
        .map((r) => `<span title="${r.name}">${r.emoji}</span>`)
        .join(" ");

      overlay.innerHTML = `
        <div class="versus-card">
          <div class="versus-card__head">
            <h2>⛏️ Floor ${current.floor} cleared!</h2>
            <!-- No close button: bank or descend is a mandatory choice -->
            <span class="versus-card__close" style="visibility:hidden" aria-hidden="true">✕</span>
          </div>
          <div class="gauntlet-pot">
            <span class="gauntlet-pot__value">🪙 ${current.pot}</span>
            <span class="gauntlet-pot__label">run pot · +${loot} loot${owned ? ` · relics: ${owned}` : ""}</span>
          </div>
          ${relicShop ? `<p class="versus-card__sub">Relic shop — paid from the pot:</p>
          <div class="gauntlet-relics">${relicShop}</div>` : ""}
          <div class="versus-card__actions">
            <button class="versus-card__btn versus-card__btn--done gauntlet-bank">🏦 Bank 🪙 ${current.pot} &amp; escape</button>
            <button class="versus-card__btn versus-card__btn--rematch gauntlet-descend">⬇️ Descend (pot ×1.25)</button>
          </div>
        </div>
      `;

      overlay.querySelectorAll<HTMLButtonElement>(".gauntlet-relic__buy").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (buyRelic(btn.dataset["relic"]!)) {
            refreshModeChrome();
            draw();
          }
        });
      });
      overlay.querySelector(".gauntlet-bank")!.addEventListener("click", () => {
        const banked = bankRun();
        gauntletGame = false;
        overlay.remove();
        showToast(`🏦 Banked 🪙 ${banked} Clash Coins — run complete!`);
        startGame();
      });
      overlay.querySelector(".gauntlet-descend")!.addEventListener("click", () => {
        overlay.remove();
        descend();
        startGauntletFloor();
      });
    };

    draw();
    document.body.appendChild(overlay);
  }

  // ── Gauntlet run-over screen (6D) ──
  function showGauntletRunOver(floorReached: number, potLost: number, timeSecs: number): void {
    document.querySelector(".versus-overlay")?.remove();

    const stats = getGauntletStats();
    const newBest = floorReached >= stats.bestFloor;
    const shareLine = `⛏️ GridClash Gauntlet — fell on floor ${floorReached} with 🪙 ${potLost} unbanked (${timeSecs}s on the last floor)`;

    const overlay = document.createElement("div");
    overlay.className = "versus-overlay";
    overlay.innerHTML = `
      <div class="versus-card versus-card--result">
        <div class="versus-card__head">
          <h2>💥 The Gauntlet claims another</h2>
          <button class="versus-card__close" aria-label="Close">✕</button>
        </div>
        <p class="campaign-result__subtitle">
          Fell on floor <strong>${floorReached}</strong>${newBest && floorReached > 0 ? " — a personal best!" : ""}
        </p>
        <p class="campaign-result__subtitle">🪙 ${potLost} unbanked coins lost to the depths.</p>
        <p class="campaign-result__tip">Best floor: ${stats.bestFloor} · Biggest bank: 🪙 ${stats.biggestBank}</p>
        <div class="versus-card__actions">
          <button class="versus-card__btn versus-card__btn--rematch gauntlet-again">New Run</button>
          <button class="versus-card__btn gauntlet-share">Copy result</button>
          <button class="versus-card__btn versus-card__btn--done gauntlet-done">Back to solo</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => overlay.remove();
    overlay.querySelector(".versus-card__close")!.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    overlay.querySelector(".gauntlet-again")!.addEventListener("click", () => {
      close();
      startRun();
      startGauntletFloor();
    });
    overlay.querySelector(".gauntlet-share")!.addEventListener("click", () => {
      navigator.clipboard?.writeText(shareLine).then(
        () => showToast("📋 Result copied!"),
        () => showToast("❌ Couldn't copy")
      );
    });
    overlay.querySelector(".gauntlet-done")!.addEventListener("click", () => {
      close();
      startGame();
    });
  }

  // ── Campaign level starter (6B) ──
  // Seeded boards (same board every attempt), optional countdown, star
  // ratings. No replay submission — campaign boards aren't leaderboard runs.
  function startCampaignLevel(levelNum: number): void {
    const level = getCampaignLevel(levelNum);
    if (!level || !isLevelUnlocked(levelNum)) return;

    currentDifficulty = toDifficultyConfig(level);
    endShown = false;
    resetGameTracking();
    hintsAllowed = true;
    modal.close();

    activeMatch?.abort();
    activeMatch = null;
    hud.hideOpponentPanel();

    document.getElementById("fireworks-canvas")?.remove();
    stopFireworksSound?.();
    stopFireworksSound = null;

    mascot.textContent = activeMascotEmoji();
    mascot.classList.remove("mascot-btn--win", "mascot-btn--lose", "mascot-btn--unlock");

    // Sever old engine callbacks before replacing (same stale-state fix as startGame)
    if (engine) {
      engine.onStateChange = null;
      engine.onGameEnded   = null;
    }

    engine = new GridCrashEngine(currentDifficulty, "random", level.seed);

    engine.onStateChange = (state) => {
      board.update(state);
      hud.update(state, engine.minesRemaining);
      sidebar.update(state, engine.minesRemaining);
      refreshModeChrome();

      if ((state.phase === "WIN" || state.phase === "LOSE") && !endShown) {
        endShown = true;

        const unlockResult = addToLifetimeScore(state.score);
        awardCoins(state.score, state.difficulty.id, false);
        updateMascot(mascot, state.phase, unlockResult.justUnlocked);

        if (unlockResult.justUnlocked.length > 0) {
          unlockResult.justUnlocked.forEach((emoji) => {
            showToast(`🎉 New mascot unlocked: ${emoji}  Tap the mascot to equip it!`);
          });
        }

        let correctFlags = 0;
        let wrongFlags = 0;
        for (const row of state.grid) {
          for (const cell of row) {
            if (!cell.isFlagged) continue;
            if (cell.isMine) correctFlags++;
            else wrongFlags++;
          }
        }

        const achieveResult = recordGameResult({
          won: state.phase === "WIN",
          difficultyId: state.difficulty.id,
          correctFlags,
          lifetimeScore: unlockResult.newTotal,
        });
        if (achieveResult.justEarned.length > 0) {
          achieveResult.justEarned.forEach((a, i) => {
            setTimeout(() => showAchievementToast(a), i * 1200);
          });
        }

        updateQuestsAfterGame(
          state.phase === "WIN", state.difficulty.id, state.timeElapsed,
          state.score, correctFlags, false
        );

        const outcome = recordLevelResult({
          levelNum,
          won: state.phase === "WIN",
          timeSecs: state.timeElapsed,
          wrongFlags,
          hintsUsed: hintsUsedThisGame,
        });
        if (outcome.coinsAwarded > 0) {
          setTimeout(() => {
            showToast(`⛰️ Level ${levelNum} first clear! +${outcome.coinsAwarded} Clash Coins`);
          }, 1000);
        }

        if (state.phase === "WIN") {
          playWinAnimation(10000);
          stopFireworksSound = playFireworks(10000);
          hapticSuccess();
        }
        if (state.phase === "LOSE") {
          playExplosion();
          hapticHeavy();
        }

        setTimeout(() => showCampaignResult(levelNum, state.phase === "WIN", state.timeElapsed, outcome), 600);
      }
    };

    engine.onGameEnded = (_score, _key) => {};

    const initialState = engine.getState();
    board.initialize(initialState);
    hud.update(initialState, engine.minesRemaining);
    sidebar.update(initialState, engine.minesRemaining);
    refreshModeChrome();
  }

  // ── Campaign result screen (6B) ──
  function showCampaignResult(
    levelNum: number,
    won: boolean,
    timeSecs: number,
    outcome: LevelOutcome
  ): void {
    document.querySelector(".versus-overlay")?.remove();

    const level = getCampaignLevel(levelNum)!;
    const best = getLevelStars(levelNum);
    const hasNext = won && levelNum < CAMPAIGN_SIZE;

    const starsRow = [1, 2, 3]
      .map((s) => `<span class="campaign-result__star${s <= outcome.stars ? " campaign-result__star--earned" : ""}">★</span>`)
      .join("");

    const subtitle = won
      ? (timeSecs <= level.parTimeSecs
          ? `Cleared in ${timeSecs}s — under par (${level.parTimeSecs}s)!`
          : `Cleared in ${timeSecs}s · par is ${level.parTimeSecs}s`)
      : (level.timeLimitSecs !== undefined && timeSecs >= level.timeLimitSecs
          ? "⏱ Time's up!"
          : "💥 Mine detonated");

    const overlay = document.createElement("div");
    overlay.className = "versus-overlay";
    overlay.innerHTML = `
      <div class="versus-card campaign-result">
        <div class="versus-card__head">
          <h2>${won ? "⛰️ Level " + levelNum + " cleared!" : "⛰️ Level " + levelNum}</h2>
          <button class="versus-card__close" aria-label="Close">✕</button>
        </div>
        <div class="campaign-result__stars">${won ? starsRow : ""}</div>
        <p class="campaign-result__subtitle">${subtitle}</p>
        ${won && best > outcome.stars ? `<p class="campaign-result__best">Best: ${"★".repeat(best)}</p>` : ""}
        ${won && outcome.stars < 3 ? `<p class="campaign-result__tip">3★ needs par time, no wrong flags, and no hints.</p>` : ""}
        <div class="versus-card__actions">
          ${hasNext ? `<button class="versus-card__btn versus-card__btn--rematch campaign-result__next">Next Level ▶</button>` : ""}
          <button class="versus-card__btn campaign-result__retry">${won ? "Replay for stars" : "Retry"}</button>
          <button class="versus-card__btn versus-card__btn--done campaign-result__map">Level Map</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => overlay.remove();
    overlay.querySelector(".versus-card__close")!.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    overlay.querySelector(".campaign-result__retry")!.addEventListener("click", () => {
      close();
      startCampaignLevel(levelNum);
    });
    overlay.querySelector(".campaign-result__map")!.addEventListener("click", () => {
      close();
      void modal.showCampaign();
    });
    overlay.querySelector(".campaign-result__next")?.addEventListener("click", () => {
      close();
      startCampaignLevel(levelNum + 1);
    });
  }

  // ── Versus setup: pick the opponent skill (also sets the board difficulty) ──
  function showVersusSetup(): void {
    document.querySelector(".versus-overlay")?.remove();

    const overlay = document.createElement("div");
    overlay.className = "versus-overlay";
    overlay.innerHTML = `
      <div class="versus-card">
        <div class="versus-card__head">
          <h2>⚔️ Versus Match</h2>
          <button class="versus-card__close" aria-label="Close">✕</button>
        </div>
        <p class="versus-card__blurb">
          Race an opponent on your own board. <strong>Highest score wins</strong> —
          a careful blow-up can still beat a sloppy clear. No human handy? Take on the bot.
        </p>
        <p class="versus-card__sub">Choose your opponent:</p>
        <div class="versus-card__tiers">
          <button class="versus-tier" data-skill="easy">
            <span class="versus-tier__emoji">🤖</span>
            <span class="versus-tier__name">Rookie Bot</span>
            <span class="versus-tier__detail">Easy board · makes mistakes</span>
          </button>
          <button class="versus-tier" data-skill="medium">
            <span class="versus-tier__emoji">🦾</span>
            <span class="versus-tier__name">Veteran Bot</span>
            <span class="versus-tier__detail">Medium board · steady</span>
          </button>
          <button class="versus-tier" data-skill="hard">
            <span class="versus-tier__emoji">🧠</span>
            <span class="versus-tier__name">Master Bot</span>
            <span class="versus-tier__detail">Expert board · 5% slip chance</span>
          </button>
        </div>
        <p class="versus-card__foot">Real-time human matchmaking is coming soon.</p>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => overlay.remove();
    overlay.querySelector(".versus-card__close")!.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });

    overlay.querySelectorAll<HTMLButtonElement>(".versus-tier").forEach((btn) => {
      btn.addEventListener("click", () => {
        const skill = btn.dataset["skill"] as CpuSkill;
        close();
        startVersusGame(skill);
      });
    });
  }

  // ── Versus game starter ──
  // Independent of solo/daily. Human plays a normal board; the CPU races in
  // parallel. Match resolves by SCORE when both sides finish. Human scores
  // still submit to the leaderboard on a win via the normal end-game modal.
  function startVersusGame(skill: CpuSkill): void {
    const boardMap: Record<CpuSkill, string> = { easy: "easy", medium: "medium", hard: "hard" };
    currentDifficulty = DIFFICULTIES[boardMap[skill]];
    endShown = false;
    resetGameTracking();
    hintsAllowed = false; // competitive mode — no paid reveals
    modal.close();

    document.getElementById("fireworks-canvas")?.remove();
    stopFireworksSound?.();
    stopFireworksSound = null;

    mascot.textContent = activeMascotEmoji();
    mascot.classList.remove("mascot-btn--win", "mascot-btn--lose", "mascot-btn--unlock");

    // Set up the match
    activeMatch?.abort();
    activeMatch = new VersusMatch(skill);
    hud.showOpponentPanel(activeMatch.opponentLabel);
    activeMatch.onOpponentUpdate = (snap) => {
      hud.updateOpponentPanel(snap.score, snap.progress, snap.alive, snap.finished);
    };
    activeMatch.onMatchEnd = (detail) => showVersusResult(detail);

    // Sever old engine callbacks before replacing
    if (engine) {
      engine.onStateChange = null;
      engine.onGameEnded   = null;
    }

    const vsSeed = newGameSeed();
    const vsMode = generationMode;
    engine = new GridCrashEngine(currentDifficulty, vsMode, vsSeed);

    let matchStarted = false;

    engine.onStateChange = (state) => {
      board.update(state);
      hud.update(state, engine.minesRemaining);
      sidebar.update(state, engine.minesRemaining);

      // Start the CPU race on the human's first reveal so both begin together.
      if (state.phase === "PLAY" && !matchStarted) {
        matchStarted = true;
        activeMatch?.start();
      }

      if ((state.phase === "WIN" || state.phase === "LOSE") && !endShown) {
        endShown = true;

        // Lifetime score + unlocks + achievements apply to versus games too.
        const unlockResult = addToLifetimeScore(state.score);
        awardCoins(state.score, state.difficulty.id, false);
        if (state.phase === "WIN") {
          submitWinReplay(state.score, state.timeElapsed, state.difficulty.id, false, vsSeed, vsMode);
        }
        updateMascot(mascot, state.phase, unlockResult.justUnlocked);
        if (unlockResult.justUnlocked.length > 0) {
          unlockResult.justUnlocked.forEach((emoji) => {
            showToast(`🎉 New mascot unlocked: ${emoji}  Tap the mascot to equip it!`);
          });
        }

        let correctFlags = 0;
        for (const row of state.grid)
          for (const cell of row)
            if (cell.isFlagged && cell.isMine) correctFlags++;

        const achieveResult = recordGameResult({
          won: state.phase === "WIN",
          difficultyId: state.difficulty.id,
          correctFlags,
          lifetimeScore: unlockResult.newTotal,
        });
        if (achieveResult.justEarned.length > 0) {
          achieveResult.justEarned.forEach((a, i) => {
            setTimeout(() => showAchievementToast(a), i * 1200);
          });
        }

        updateQuestsAfterGame(
          state.phase === "WIN", state.difficulty.id, state.timeElapsed,
          state.score, correctFlags, false
        );

        if (state.phase === "WIN") {
          playWinAnimation(10000);
          stopFireworksSound = playFireworks(10000);
          hapticSuccess();
        }
        if (state.phase === "LOSE") {
          playExplosion();
          hapticHeavy();
        }

        // Hand the human's outcome + score to the match. The engine already
        // computes the bonus-free score on a LOSE, which is exactly what the
        // match needs. The match resolves once the CPU is also done.
        if (state.phase === "WIN") {
          activeMatch?.humanWon(state.score, state.timeElapsed);
        } else {
          activeMatch?.humanLost(state.score, state.timeElapsed);
        }

        // Solo end-game modal still shows (score submission to leaderboard lives there).
        setTimeout(() => modal.showGameEnd(state, generationMode), 600);
      }
    };

    engine.onGameEnded = (_score, _key) => {};

    const initialState = engine.getState();
    board.initialize(initialState);
    hud.update(initialState, engine.minesRemaining);
    sidebar.update(initialState, engine.minesRemaining);
    refreshModeChrome();
  }

  // ── Versus result screen ──
  function showVersusResult(detail: MatchDetail): void {
    document.querySelector(".versus-overlay")?.remove();

    const title = detail.result === "human_win" ? "🏆 You Win!"
                : detail.result === "cpu_win"   ? "😤 Opponent Wins"
                : "🤝 Draw";

    const fmt = (s: number | null): string => {
      if (s === null) return "—";
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
    };
    const humanOutcome = detail.humanCleared ? "Cleared" : "Blew up";
    const cpuOutcome   = detail.cpuCleared   ? "Cleared" : "Blew up";

    const overlay = document.createElement("div");
    overlay.className = "versus-overlay";
    overlay.innerHTML = `
      <div class="versus-card versus-card--result versus-card--${detail.result}">
        <div class="versus-card__head">
          <h2>${title}</h2>
          <button class="versus-card__close" aria-label="Close">✕</button>
        </div>
        <div class="versus-result">
          <div class="versus-result__side versus-result__side--you">
            <div class="versus-result__who">You</div>
            <div class="versus-result__score">${detail.humanScore}</div>
            <div class="versus-result__meta">${humanOutcome} · ${fmt(detail.humanTimeSecs)}</div>
          </div>
          <div class="versus-result__vs">vs</div>
          <div class="versus-result__side versus-result__side--cpu">
            <div class="versus-result__who">${detail.cpuLabel}</div>
            <div class="versus-result__score">${detail.cpuScore}</div>
            <div class="versus-result__meta">${cpuOutcome} · ${fmt(detail.cpuTimeSecs)}</div>
          </div>
        </div>
        <div class="versus-card__actions">
          <button class="versus-card__btn versus-card__btn--rematch">Rematch</button>
          <button class="versus-card__btn versus-card__btn--done">Back to solo</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const close = (): void => overlay.remove();
    overlay.querySelector(".versus-card__close")!.addEventListener("click", close);
    overlay.querySelector(".versus-card__btn--done")!.addEventListener("click", () => {
      close();
      startGame();
    });
    overlay.querySelector(".versus-card__btn--rematch")!.addEventListener("click", () => {
      close();
      startVersusGame(detail.cpuSkill);
    });
  }

  startGame();
}

// ─── Boot ────────────────────────────────────────────────────────────────────

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

